/* eslint-disable no-unused-vars */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { localStorageIdGenerator } from "../../libraries/picoxweb/internal/utils/generators";

import {
  campaignAttributionHasSeoReferrer,
  campaignAttributionReadReferrerHostname,
} from "./campaignAttribution";

export const UTM_PARAMS_STORAGE_KEY = "utmParameters";

const PARAM_NAMES = [
  "v",
  "gclid",
  "fbclid",
  "ttclid",
  "t_s",
  "t_cid",
  "t_cname",
  "t_agid",
  "t_agname",
  "t_crid",
  "t_crname",
  "t_match_type",
  "t_network",
  "t_device",
  "t_gcid",
  "t_validation",
];

/**
 * Extract the UTM params for the current url.
 * @returns An object where each property P : P in PARAM_NAMES and P=V in current url query string.
 */
function getUTMParamsFromQuery() {
  const queryEntries = window.URLSearchParams
    ? Array.from(new URLSearchParams(window.location.search).entries())
    : [];

  const params: Record<string, any> = {};
  queryEntries.forEach(([key, val]) => {
    if (PARAM_NAMES.includes(key)) {
      params[key] = val || undefined;
    }
  });
  return omitUndefined(params);
}

/**
 * Process SEO parameters and add them to session storage (landing_page_info)
 * @param sessionParams Current session parameters (landing_page_info)
 * @param persistentParams Current persistent parameters
 * @returns Updated session parameters with SEO data
 */
async function processSeoParameters(
  sessionParams: Record<string, any>,
  persistentParams: Record<string, any>
) {
  const updatedParams = { ...sessionParams };

  // Check if user comes from SEO using existing function
  const isSeoTraffic = campaignAttributionHasSeoReferrer();

  // Check if t_s is populated in either session or persistent (indicates PUA)
  const hasUtmSource = sessionParams.t_s || persistentParams.t_s;

  // Only set t_network to 'seo' if:
  // 1. t_network is not already set (in session or persistent)
  // 2. t_s is not populated (indicates organic, not PUA)
  // 3. Campaign Attribution indicates SEO traffic
  if (
    !updatedParams.t_network &&
    !persistentParams.t_network &&
    isSeoTraffic &&
    !hasUtmSource
  ) {
    // Set t_network to 'seo' for organic search traffic
    updatedParams.t_network = "seo";
  }

  // Save the website source as t_wsource (only if not already present)
  // Use Campaign Attribution referrer for first-touch attribution consistency
  if (!updatedParams.t_wsource) {
    const campaignReferrerHostname = campaignAttributionReadReferrerHostname();
    if (campaignReferrerHostname) {
      updatedParams.t_wsource = campaignReferrerHostname;
    }
  }

  // Save the landing page as t_lp (only on first visit)
  if (!updatedParams.t_lp) {
    const currentUrl = window.location.href;
    updatedParams.t_lp = currentUrl;
  }

  updatedParams.t_lsid = await localStorageIdGenerator("mileiq_marketing_web")();

  return updatedParams;
}

/**
 * Updates the session and persistent UTM params.
 * Note that whenever a new subset of UTM params is retieved from the url, this completely replaces
 * -all- the UTM params stored in session or local storage. This is the intended behaviour, to avoid mixed
 * attributions.
 *
 * 1. User browsers dashboard.mileiq.com/path?utm_term=google&gclid=123
 *  - UTMs from query = {utm_term: "google", gclid="123"}
 *  - UTMs in session = {utm_term: "google", gclid="123"}
 *  - UTMs persistent = {utm_term: "google", gclid="123"}
 * 2. User navigates to dashboard.mileiq.com/path/another-path
 *  - UTMs from query = {}
 *  - UTMs in session = {utm_term: "google", gclid="123"}
 *  - UTMs persistent = {utm_term: "google", gclid="123"}
 * 3. (Unlikely) User change a utm param or navigates to a page in the tab with different utm params: dashboard.mileiq.com/another-path?utm_term=apple
 *  - UTMs from query = {utm_term: "apple"}
 *  - UTMs in session = {utm_term: "apple"}
 *  - UTMs persistent = {utm_term: "apple"}
 * 4. User closes the browser, reopens and navigates to dashboard.mileiq.com/path/another-path
 *  - UTMs from query = {}
 *  - UTMs in session = {}
 *  - UTMs persistent = {utm_term: "apple", gclid="123"}
 *
 * Any Pico event following any of these steps should have:
 * - user.info.landing_page_info = UTMs in session
 * - user.info.persistent = UTMs persistent
 */
export async function updateLandingPageAndPersistentUTMParams() {
  const utmParamsFromQuery = getUTMParamsFromQuery();
  const persistentUTMParams = parseJsonDict(
    localStorage.getItem(UTM_PARAMS_STORAGE_KEY)
  );
  const sessionUTMParams = parseJsonDict(
    sessionStorage.getItem(UTM_PARAMS_STORAGE_KEY)
  );

  const newSessionUTMParams =
    Object.keys(utmParamsFromQuery).length > 0
      ? utmParamsFromQuery
      : sessionUTMParams;

  // Process SEO parameters and add them to session storage (landing_page_info)
  const processedSessionParams = await processSeoParameters(
    newSessionUTMParams,
    persistentUTMParams
  );

  sessionStorage.setItem(
    UTM_PARAMS_STORAGE_KEY,
    JSON.stringify(processedSessionParams)
  );

  const newPersistentUTMParams =
    Object.keys(processedSessionParams).length > 0
      ? processedSessionParams
      : persistentUTMParams;

  localStorage.setItem(
    UTM_PARAMS_STORAGE_KEY,
    JSON.stringify(newPersistentUTMParams)
  );

  return [processedSessionParams, newPersistentUTMParams];
}

export default updateLandingPageAndPersistentUTMParams;

function omitBy(
  obj: Record<string, any>,
  shouldOmitFunc: (val: any, key: string) => boolean
) {
  const result: Record<string, any> = {};
  Object.entries(obj).forEach(([key, val]) => {
    if (!shouldOmitFunc(val, key)) {
      result[key] = val;
    }
  });
  return result;
}

const omitUndefined = (obj: Record<string, any>) =>
  omitBy(obj, (val) => val === undefined);

export function parseJsonDict(json: string | null, reviver = undefined) {
  if (!json) {
    return {};
  }
  try {
    const dict = JSON.parse(json, reviver);
    return dict instanceof Object ? dict : {};
  } catch {
    return {};
  }
}

export function getPersistentUTMParams() {
  // Check if we're on the server side
  if (typeof window === "undefined") {
    return {};
  }

  const persistentUTMParams = parseJsonDict(
    localStorage.getItem(UTM_PARAMS_STORAGE_KEY)
  );
  return omitUndefined(persistentUTMParams);
}

export function getPath() {
  // Check if we're on the server side
  if (typeof window === "undefined") {
    return undefined;
  }

  const path = window.location.pathname.split("/");
  if (path.length > 1 && path[1]) {
    return path[1];
  }
  return undefined;
}

/**
 * Build query parameters for download app URL.
 * This function works both server-side and client-side.
 *
 * @param username - The username to include in the URL
 * @param source - The source identifier (e.g., 'on', 'td', 'std', 'ongs', 'get_app')
 * @param platform - Optional platform identifier (e.g., 'android', 'ios')
 * @returns Object with query parameters for download app URL
 */
export function buildDownloadAppQueryParams(
  username: string | null,
  source: string,
  platform?: string
) {
  const utmParams = getPersistentUTMParams();
  const path = getPath();

  const queryParams: Record<string, string> = {
    ...(username ? { u: username } : {}),
    ...(source ? { s: source } : {}),
    ...(platform ? { p: platform } : {}),
    ...(utmParams.t_lp ? { t_lp: utmParams.t_lp } : {}),
    ...(utmParams.t_wsource ? { t_wsource: utmParams.t_wsource } : {}),
    ...(utmParams.t_network ? { t_network: utmParams.t_network } : {}),
    ...(utmParams.t_lsid ? { t_lsid: utmParams.t_lsid } : {}),
    ...(path ? { t_cta_path: path } : {}),
  };

  return queryParams;
}
